import type { CrmLead, OperationsState } from './operations.types';

export const sourceLabels: Record<string, string> = { walk_in: '自然到店', meituan: '美团', douyin: '抖音', xiaohongshu: '小红书', wechat: '微信私域', referral: '转介绍', event: '活动', other: '其他' };
export const stageLabels: Record<string, string> = { new: '新客资', contacted: '已联系', trial_booked: '已约体验', trial_arrived: '已到店', quoted: '已报价', won: '已成交', lost: '已流失' };
export const money = (cents: number) => `¥${(cents / 100).toLocaleString('zh-CN', { maximumFractionDigits: 2 })}`;
export const timeLabel = (value: string | null) => value ? new Date(value).toLocaleString('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) : '未设置';
export const isLeadOverdue = (lead: CrmLead) => Boolean(lead.nextFollowUpAt && new Date(lead.nextFollowUpAt) < new Date() && !['won', 'lost'].includes(lead.stage));

export function operationsSummary(state: OperationsState, storeId?: string | null) {
  const inStore = <T extends { storeId: string | null }>(items: T[]) => storeId ? items.filter((item) => item.storeId === storeId) : items;
  const leads = inStore(state.leads);
  const closings = inStore(state.closings);
  const alerts = inStore(state.alerts);
  const risks = inStore(state.memberRisks);
  const trainings = inStore(state.trainings);
  const won = leads.filter((item) => item.stage === 'won').length;
  const arrived = leads.filter((item) => ['trial_arrived', 'quoted', 'won'].includes(item.stage)).length;
  const openAlerts = alerts.filter((item) => item.status !== 'resolved');
  return {
    activeLeads: leads.filter((item) => !['won', 'lost'].includes(item.stage)).length,
    overdueLeads: leads.filter(isLeadOverdue).length,
    conversionRate: arrived ? Math.round(won / arrived * 100) : 0,
    pendingClosings: closings.filter((item) => item.status === 'submitted').length,
    closingDifferenceCents: closings.filter((item) => item.status !== 'rejected').reduce((sum, item) => sum + Math.abs(item.differenceCents), 0),
    openAlerts: openAlerts.length,
    criticalAlerts: openAlerts.filter((item) => item.severity === 'critical').length,
    openMemberRisks: risks.filter((item) => item.status !== 'resolved').length,
    overdueTrainings: trainings.filter((item) => !['passed', 'failed'].includes(item.status) && new Date(item.dueAt) < new Date()).length,
  };
}

export function buildOperationsBrief(state: OperationsState, storeId?: string | null) {
  const summary = operationsSummary(state, storeId);
  const priorities: string[] = [];
  if (summary.overdueLeads) priorities.push(`今天先闭环${summary.overdueLeads}条超时客资，避免体验意向继续降温。`);
  if (summary.pendingClosings) priorities.push(`完成${summary.pendingClosings}份门店日结审核，优先处理账实差异。`);
  if (summary.openMemberRisks) priorities.push(`为${summary.openMemberRisks}名风险会员明确负责人和下一次联系时间。`);
  if (summary.criticalAlerts) priorities.push(`立即处理${summary.criticalAlerts}项高危经营预警并留下处置记录。`);
  if (summary.overdueTrainings) priorities.push(`补齐${summary.overdueTrainings}项逾期培训，避免人员能力断层。`);
  if (!priorities.length) priorities.push('当前没有高优先级异常，建议复盘渠道转化并准备下一轮增长实验。');
  return {
    title: storeId ? '门店运营行动建议' : '总部四店运营行动建议',
    summary: `当前有${summary.activeLeads}条进行中客资、${summary.openAlerts}项未闭环预警、${summary.openMemberRisks}名风险会员；体验到店后的成交率为${summary.conversionRate}%。`,
    priorities,
  };
}
